import React, { useState, useEffect, useCallback } from "react";
import {
  Card, Tabs, Table, Button, Space, Typography, Tag, Modal, Form, Input,
  Switch, Tooltip, Select, message, Popconfirm, Badge, Divider, Alert,
  Row, Col,
} from "antd";
import {
  PrinterOutlined, PlusOutlined, EditOutlined, DeleteOutlined,
  CheckCircleOutlined, CopyOutlined, ArrowUpOutlined, ArrowDownOutlined,
  SettingOutlined, EyeOutlined, EyeInvisibleOutlined,
} from "@ant-design/icons";
import {
  listTemplates, createTemplate, updateTemplate, setDefaultTemplate,
  deleteTemplate, seedDefaultTemplates,
} from "../../api/printTemplateApi";
import PageTitle from "../page-header/PageHeader";

const { Title, Text } = Typography;

const STAGE_META = [
  { code: "corrugation",     label: "Corrugation" },
  { code: "sheet_cutting",   label: "Sheet Cutting" },
  { code: "pasting",         label: "Pasting" },
  { code: "casting",         label: "Casting" },
  { code: "die_cutting",     label: "Die Cutting" },
  { code: "pre_print",       label: "Pre-Print" },
  { code: "after_box_print", label: "After-Box Print" },
  { code: "joining",         label: "Joining / Stitching" },
  { code: "coating",         label: "Coating" },
  { code: "qc",              label: "Quality Check" },
];

const SUPPORTED_LANGS = [
  { code: "en", label: "English (Primary)" },
  { code: "hi", label: "हिंदी (Hindi)" },
];

// ── Complete field catalogue — Order + OrderItem + Job, deduplicated by key ───
// "group" is used for display grouping in the Add Column picker only.
const ALL_FIELDS = (() => {
  const raw = [
    // ── Job identification ────────────────────────────────────
    { key: "job_number",          labels: { en: "Job #",               hi: "काम नं." },           group: "Job" },
    { key: "item_name",           labels: { en: "Item Name",           hi: "वस्तु नाम" },         group: "Item" },
    { key: "subcategory",         labels: { en: "Box Type",            hi: "बक्सा प्रकार" },      group: "Item" },
    { key: "item_description",    labels: { en: "Item Description",    hi: "वस्तु विवरण" },       group: "Item" },
    { key: "item_remarks",        labels: { en: "Remarks",             hi: "टिप्पणी" },            group: "Item" },
    { key: "notes",               labels: { en: "Notes",               hi: "नोट्स" },              group: "Item" },
    // ── Dimensions ────────────────────────────────────────────
    { key: "length",              labels: { en: "Length (L)",          hi: "लंबाई (L)" },          group: "Dimensions" },
    { key: "width",               labels: { en: "Width (W)",           hi: "चौड़ाई (W)" },        group: "Dimensions" },
    { key: "height",              labels: { en: "Height (H)",          hi: "ऊंचाई (H)" },          group: "Dimensions" },
    { key: "unit_type",           labels: { en: "Unit",                hi: "इकाई" },               group: "Dimensions" },
    // ── Sheet sizes ────────────────────────────────────────────
    { key: "roll_size",           labels: { en: "RS\"",                hi: "RS\"" },               group: "Sheet Sizes" },
    { key: "cutting_size",        labels: { en: "CS\"",                hi: "CS\"" },               group: "Sheet Sizes" },
    { key: "roll_size_2",         labels: { en: "RS2\"",               hi: "RS2\"" },              group: "Sheet Sizes" },
    { key: "cutting_size_2",      labels: { en: "CS2\"",               hi: "CS2\"" },              group: "Sheet Sizes" },
    { key: "rs",                  labels: { en: "RS (calc)",           hi: "RS (calc)" },          group: "Sheet Sizes" },
    { key: "cs",                  labels: { en: "CS (calc)",           hi: "CS (calc)" },          group: "Sheet Sizes" },
    { key: "boxes_per_sheet",     labels: { en: "M (bx/sheet)",        hi: "M (बक्से/शीट)" },    group: "Sheet Sizes" },
    { key: "sheets_per_box",      labels: { en: "N (sh/box)",          hi: "N (शीट/बक्सा)" },    group: "Sheet Sizes" },
    { key: "box_type",            labels: { en: "Box Style",           hi: "बक्सा शैली" },        group: "Sheet Sizes" },
    // ── Material specs ─────────────────────────────────────────
    { key: "ply",                 labels: { en: "Ply",                 hi: "प्लाई" },              group: "Specs" },
    { key: "casting_type",        labels: { en: "Casting Type",        hi: "ढलाई प्रकार" },       group: "Specs" },
    { key: "net_gsm",             labels: { en: "Net GSM",             hi: "नेट GSM" },            group: "Specs" },
    { key: "box_weight",          labels: { en: "Box Weight (kg)",     hi: "बक्सा वजन (किग्रा)" }, group: "Specs" },
    { key: "joining_method",      labels: { en: "Joining Method",      hi: "जोड़ाई विधि" },       group: "Specs" },
    // ── Printing ───────────────────────────────────────────────
    { key: "is_printing",         labels: { en: "Printing?",           hi: "छपाई?" },              group: "Printing" },
    { key: "print_method",        labels: { en: "Print Method",        hi: "छपाई विधि" },         group: "Printing" },
    { key: "printing_sides",      labels: { en: "Print Sides",         hi: "छपाई साइड" },         group: "Printing" },
    { key: "printing_colors",     labels: { en: "Print Colors",        hi: "रंग" },                group: "Printing" },
    { key: "printing_special_colors", labels: { en: "Special Colors",  hi: "विशेष रंग" },         group: "Printing" },
    // ── Coating ────────────────────────────────────────────────
    { key: "is_coating",          labels: { en: "Coating?",            hi: "कोटिंग?" },            group: "Coating" },
    // ── Stage detail — common keys from unified_table ──────────
    { key: "box_qty",             labels: { en: "Box Qty",             hi: "बक्सा" },              group: "Stage Detail" },
    { key: "sheet_qty",           labels: { en: "Sheet Qty",           hi: "शीट" },                group: "Stage Detail" },
    { key: "waste",               labels: { en: "Waste\"",             hi: "बर्बादी\"" },          group: "Stage Detail" },
    { key: "M",                   labels: { en: "M",                   hi: "M" },                  group: "Stage Detail" },
    { key: "N",                   labels: { en: "N",                   hi: "N" },                  group: "Stage Detail" },
    { key: "L",                   labels: { en: "L\"",                 hi: "लं.\"" },               group: "Stage Detail" },
    { key: "B",                   labels: { en: "W\"",                 hi: "चौ.\"" },               group: "Stage Detail" },
    { key: "H",                   labels: { en: "H\"",                 hi: "ऊं.\"" },               group: "Stage Detail" },
    // ── Corrugation stage ──────────────────────────────────────
    { key: "liner_material",      labels: { en: "Liner Material",      hi: "लाइनर सामग्री" },     group: "Corrugation" },
    { key: "liner_kg",            labels: { en: "Liner (kg)",          hi: "लाइनर (किग्रा)" },    group: "Corrugation" },
    { key: "flute_material",      labels: { en: "Flute Material",      hi: "नालीदार सामग्री" },   group: "Corrugation" },
    { key: "flute_kg",            labels: { en: "Flute (kg)",          hi: "नालीदार (किग्रा)" },  group: "Corrugation" },
    { key: "flute_factor",        labels: { en: "Flute Factor (FF)",   hi: "नालीदार गुणक" },      group: "Corrugation" },
    { key: "total_paper_kg",      labels: { en: "Total Paper (kg)",    hi: "कुल कागज (किग्रा)" }, group: "Corrugation" },
    // ── Sheet cutting stage ────────────────────────────────────
    { key: "two_ply_sheets",      labels: { en: "2-Ply Sheets",        hi: "दो-प्लाई शीट" },      group: "Sheet Cutting" },
    { key: "two_ply_material",    labels: { en: "2-Ply Material",      hi: "दो-प्लाई सामग्री" },  group: "Sheet Cutting" },
    { key: "two_ply_kg",          labels: { en: "2-Ply (kg)",          hi: "दो-प्लाई (किग्रा)" }, group: "Sheet Cutting" },
    { key: "top_paper_sheets",    labels: { en: "Top Sheets",          hi: "टॉप शीट" },            group: "Sheet Cutting" },
    { key: "top_paper_material",  labels: { en: "Top Material",        hi: "टॉप सामग्री" },       group: "Sheet Cutting" },
    { key: "top_paper_kg",        labels: { en: "Top (kg)",            hi: "टॉप (किग्रा)" },       group: "Sheet Cutting" },
    // ── Pasting / gum stages ───────────────────────────────────
    { key: "passes",              labels: { en: "Passes",              hi: "बार" },                group: "Pasting" },
    { key: "gum_kg",              labels: { en: "Gum (kg)",            hi: "गोंद (किग्रा)" },      group: "Pasting" },
    // ── Quantities ─────────────────────────────────────────────
    { key: "ordered_quantity",    labels: { en: "Ordered Qty",         hi: "आदेश मात्रा" },        group: "Quantities" },
    { key: "production_quantity", labels: { en: "Production Qty",      hi: "उत्पादन मात्रा" },     group: "Quantities" },
    { key: "completed_quantity",  labels: { en: "Completed Qty",       hi: "पूर्ण मात्रा" },       group: "Quantities" },
    { key: "rejected_quantity",   labels: { en: "Rejected Qty",        hi: "अस्वीकृत मात्रा" },   group: "Quantities" },
    { key: "dispatched_quantity", labels: { en: "Dispatched Qty",      hi: "भेजी मात्रा" },        group: "Quantities" },
    { key: "quantity",            labels: { en: "Order Qty",           hi: "आदेश मात्रा" },        group: "Quantities" },
    // ── Customer & Order-level ─────────────────────────────────
    { key: "customer_name",       labels: { en: "Customer Name",       hi: "ग्राहक नाम" },         group: "Customer & Order" },
    { key: "customer_po_number",  labels: { en: "Customer PO #",       hi: "ग्राहक PO नं." },      group: "Customer & Order" },
    { key: "order_number",        labels: { en: "Order #",             hi: "आदेश नं." },            group: "Customer & Order" },
    { key: "expected_delivery",   labels: { en: "Delivery Date",       hi: "डिलीवरी तारीख" },      group: "Customer & Order" },
    { key: "priority",            labels: { en: "Priority",            hi: "प्राथमिकता" },          group: "Customer & Order" },
    { key: "shipping_address",    labels: { en: "Shipping Address",    hi: "भेजने का पता" },        group: "Customer & Order" },
    // ── Pricing (OrderItem) ─────────────────────────────────────
    { key: "rate_per_unit",       labels: { en: "Rate/Unit (₹)",       hi: "दर/इकाई (₹)" },        group: "Pricing" },
    { key: "total_amount",        labels: { en: "Total Amount (₹)",    hi: "कुल राशि (₹)" },       group: "Pricing" },
    // ── Job status ─────────────────────────────────────────────
    { key: "current_stage",       labels: { en: "Stage",               hi: "चरण" },                group: "Job Status" },
    { key: "status",              labels: { en: "Status",              hi: "स्थिति" },              group: "Job Status" },
    { key: "fulfillment_status",  labels: { en: "Material Status",     hi: "सामग्री स्थिति" },     group: "Job Status" },
    { key: "batch_number",        labels: { en: "Batch #",             hi: "बैच नं." },             group: "Job Status" },
  ];
  // Deduplicate by key — first occurrence wins
  const seen = new Set();
  return raw.filter(({ key }) => (seen.has(key) ? false : seen.add(key)));
})();

// ── Column editor row ──────────────────────────────────────────────────────────
const ColumnRow = ({ col, index, total, onChange, onMove }) => (
  <div style={{
    display: "flex", alignItems: "flex-start", gap: 8, padding: "8px 10px",
    background: col.visible ? "#fff" : "#fafafa",
    border: "1px solid #e8e8e8", borderRadius: 6, marginBottom: 6,
    opacity: col.visible ? 1 : 0.6,
  }}>
    {/* Visibility toggle */}
    <Tooltip title={col.visible ? "Visible in print" : "Hidden"}>
      <Button
        size="small"
        type="text"
        icon={col.visible ? <EyeOutlined style={{ color: "#52c41a" }} /> : <EyeInvisibleOutlined style={{ color: "#bbb" }} />}
        onClick={() => onChange(index, { visible: !col.visible })}
      />
    </Tooltip>

    {/* Key badge */}
    <Tag style={{ fontFamily: "monospace", fontSize: 11, minWidth: 90, textAlign: "center", marginTop: 3 }}>
      {col.key}
    </Tag>

    {/* Language labels */}
    <div style={{ flex: 1, display: "flex", gap: 8, flexWrap: "wrap" }}>
      {SUPPORTED_LANGS.map(({ code, label }) => (
        <div key={code} style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <Text type="secondary" style={{ fontSize: 11, whiteSpace: "nowrap" }}>{label}:</Text>
          <Input
            size="small"
            style={{ width: 140, fontSize: 12 }}
            value={col.labels?.[code] || ""}
            placeholder={code === "en" ? col.key : ""}
            onChange={(e) => {
              const labels = { ...(col.labels || {}), [code]: e.target.value };
              onChange(index, { labels });
            }}
          />
        </div>
      ))}
    </div>

    {/* Width */}
    <Input
      size="small"
      style={{ width: 60, fontSize: 12 }}
      value={col.width || ""}
      placeholder="width%"
      onChange={(e) => onChange(index, { width: e.target.value })}
    />

    {/* Align */}
    <Select
      size="small"
      style={{ width: 80 }}
      value={col.align || "left"}
      options={[{ value: "left", label: "Left" }, { value: "center", label: "Center" }, { value: "right", label: "Right" }]}
      onChange={(v) => onChange(index, { align: v })}
    />

    {/* Bold toggle */}
    <Tooltip title={col.bold ? "Bold — click to remove" : "Not bold — click to bold"}>
      <Button
        size="small"
        type={col.bold ? "primary" : "default"}
        style={{ fontWeight: 700, minWidth: 32 }}
        onClick={() => onChange(index, { bold: !col.bold })}
      >
        B
      </Button>
    </Tooltip>

    {/* Order controls */}
    <Space size={2}>
      <Button size="small" icon={<ArrowUpOutlined />}   disabled={index === 0}         onClick={() => onMove(index, -1)} />
      <Button size="small" icon={<ArrowDownOutlined />} disabled={index === total - 1}  onClick={() => onMove(index, +1)} />
    </Space>
  </div>
);

// ── Template editor modal ───────────────────────────────────────────────────────
const TemplateEditor = ({ template, onSave, onClose }) => {
  const [name, setName] = useState(template.name);
  const [columns, setColumns] = useState(
    (Array.isArray(template.columns) ? template.columns : []).map((c, i) => ({ ...c, order: c.order ?? i + 1 }))
      .sort((a, b) => a.order - b.order)
  );
  const [saving, setSaving] = useState(false);
  const [addPickerOpen, setAddPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");
  const [pickerSelected, setPickerSelected] = useState([]);

  const handleColChange = (idx, patch) => {
    setColumns((prev) => prev.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  };

  const handleMove = (idx, dir) => {
    setColumns((prev) => {
      const next = [...prev];
      const swapIdx = idx + dir;
      if (swapIdx < 0 || swapIdx >= next.length) return prev;
      [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
      return next.map((c, i) => ({ ...c, order: i + 1 }));
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const ordered = columns.map((c, i) => ({ ...c, order: i + 1 }));
      await onSave(template.id, { name, columns: ordered });
      message.success("Template saved");
      onClose();
    } catch (err) {
      message.error(err.response?.data?.error || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      title={
        <Space>
          <SettingOutlined />
          <span>Edit Template</span>
          {template.is_system && <Tag color="orange">System Default</Tag>}
        </Space>
      }
      onCancel={onClose}
      width={920}
      style={{ top: 20 }}
      footer={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" onClick={handleSave} loading={saving}>Save Template</Button>
        </Space>
      }
    >
      {template.is_system && (
        <Alert
          message="This is a system default template. Changes will update the default for this stage. Clone it to create an independent custom version."
          type="warning" showIcon style={{ marginBottom: 12 }}
        />
      )}

      <div style={{ marginBottom: 12 }}>
        <Text strong>Template Name: </Text>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={{ width: 320, marginLeft: 8 }}
        />
      </div>

      <Divider orientation="left" style={{ margin: "8px 0" }}>
        Columns — drag order with ↑↓, toggle visibility with the eye icon
      </Divider>

      <div style={{ maxHeight: 420, overflowY: "auto", paddingRight: 4 }}>
        {columns.map((col, idx) => (
          <ColumnRow
            key={col.key}
            col={col}
            index={idx}
            total={columns.length}
            onChange={handleColChange}
            onMove={handleMove}
          />
        ))}
      </div>

      {/* Add column button */}
      <div style={{ marginTop: 10 }}>
        <Button
          icon={<PlusOutlined />}
          size="small"
          onClick={() => { setPickerSearch(""); setPickerSelected([]); setAddPickerOpen(true); }}
        >
          Add Column
        </Button>
        <Text type="secondary" style={{ fontSize: 12, marginLeft: 12 }}>
          Hidden columns won't appear in print. Width: "10%", "80px", or blank for auto.
        </Text>
      </div>

      {/* Add Column Picker */}
      {addPickerOpen && (() => {
        const existingKeys = new Set(columns.map((c) => c.key));
        const groups = {};
        ALL_FIELDS
          .filter((f) => !existingKeys.has(f.key))
          .filter((f) => !pickerSearch || f.key.toLowerCase().includes(pickerSearch.toLowerCase()) || f.labels.en.toLowerCase().includes(pickerSearch.toLowerCase()))
          .forEach((f) => {
            if (!groups[f.group]) groups[f.group] = [];
            groups[f.group].push(f);
          });

        const togglePick = (key) =>
          setPickerSelected((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
          );

        const handleAddSelected = () => {
          const nextOrder = columns.length + 1;
          const newCols = pickerSelected.map((key, i) => {
            const def = ALL_FIELDS.find((f) => f.key === key) || { key, labels: { en: key, hi: "" } };
            return { key, visible: true, order: nextOrder + i, labels: { ...def.labels }, width: "", align: "left" };
          });
          setColumns((prev) => [...prev, ...newCols].map((c, i) => ({ ...c, order: i + 1 })));
          setAddPickerOpen(false);
        };

        return (
          <Modal
            open
            title={<Space><PlusOutlined /><span>Add Columns to Template</span></Space>}
            onCancel={() => setAddPickerOpen(false)}
            width={700}
            style={{ top: 40 }}
            footer={
              <Space>
                <Button onClick={() => setAddPickerOpen(false)}>Cancel</Button>
                <Button type="primary" disabled={!pickerSelected.length} onClick={handleAddSelected}>
                  Add {pickerSelected.length > 0 ? `${pickerSelected.length} ` : ""}Column{pickerSelected.length !== 1 ? "s" : ""}
                </Button>
              </Space>
            }
          >
            <Input.Search
              placeholder="Search fields..."
              value={pickerSearch}
              onChange={(e) => setPickerSearch(e.target.value)}
              style={{ marginBottom: 12 }}
              allowClear
            />
            {pickerSelected.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <Text strong style={{ fontSize: 12 }}>Selected: </Text>
                <Space size={4} wrap>
                  {pickerSelected.map((k) => {
                    const def = ALL_FIELDS.find((f) => f.key === k);
                    return (
                      <Tag key={k} closable onClose={() => togglePick(k)} color="blue" style={{ fontSize: 11 }}>
                        {def?.labels?.en || k}
                      </Tag>
                    );
                  })}
                </Space>
                <Divider style={{ margin: "8px 0" }} />
              </div>
            )}
            <div style={{ maxHeight: 380, overflowY: "auto" }}>
              {Object.keys(groups).length === 0 && (
                <Text type="secondary">All available fields already added, or no matches.</Text>
              )}
              {Object.entries(groups).map(([group, fields]) => (
                <div key={group} style={{ marginBottom: 12 }}>
                  <Text strong style={{ fontSize: 12, color: "#555", display: "block", marginBottom: 6 }}>
                    {group}
                  </Text>
                  <Space size={6} wrap>
                    {fields.map((f) => {
                      const selected = pickerSelected.includes(f.key);
                      return (
                        <Tag
                          key={f.key}
                          color={selected ? "blue" : "default"}
                          style={{ cursor: "pointer", fontSize: 12, padding: "3px 10px", userSelect: "none" }}
                          onClick={() => togglePick(f.key)}
                        >
                          <span style={{ fontWeight: selected ? 700 : 400 }}>{f.labels.en}</span>
                          <span style={{ fontSize: 10, color: selected ? "#a8c8ff" : "#aaa", marginLeft: 6 }}>
                            {f.key}
                          </span>
                          {f.labels.hi && (
                            <span style={{ fontSize: 10, color: selected ? "#d0e8ff" : "#bbb", marginLeft: 4 }}>
                              · {f.labels.hi}
                            </span>
                          )}
                        </Tag>
                      );
                    })}
                  </Space>
                </div>
              ))}
            </div>
          </Modal>
        );
      })()}
    </Modal>
  );
};

// ── Stage templates section ───────────────────────────────────────────────────
const StageSection = ({ stageCode, stageLabel, templates, onRefresh }) => {
  const [editTarget, setEditTarget] = useState(null);
  const [cloning, setCloning] = useState(false);

  const defaultTemplate = templates.find((t) => t.is_default);
  const systemTemplate = templates.find((t) => t.is_system);
  const customTemplates = templates.filter((t) => !t.is_system);

  const handleSave = async (id, data) => {
    await updateTemplate(id, data);
    onRefresh();
  };

  const handleSetDefault = async (id) => {
    await setDefaultTemplate(id);
    message.success("Default updated");
    onRefresh();
  };

  const handleDelete = async (id) => {
    await deleteTemplate(id);
    message.success("Template deleted");
    onRefresh();
  };

  const handleClone = async (source) => {
    setCloning(true);
    try {
      await createTemplate({
        name: `${source.name} (Copy)`,
        document_type: source.document_type,
        stage_code: source.stage_code,
        columns: source.columns,
      });
      message.success("Template cloned");
      onRefresh();
    } catch (err) {
      message.error("Clone failed");
    } finally {
      setCloning(false);
    }
  };

  const templateCard = (t) => (
    <div key={t.id} style={{
      border: `1px solid ${t.is_default ? "#52c41a" : "#e8e8e8"}`,
      borderRadius: 8, padding: "12px 16px", marginBottom: 8,
      background: t.is_default ? "#f6ffed" : "#fff",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <Space wrap>
          <Text strong>{t.name}</Text>
          {t.is_system && <Tag color="orange">System</Tag>}
          {t.is_default && <Tag color="green" icon={<CheckCircleOutlined />}>Active Default</Tag>}
          <Tag style={{ fontSize: 11 }}>
            {Array.isArray(t.columns) ? t.columns.filter((c) => c.visible).length : 0} visible columns
          </Tag>
        </Space>
        <Space>
          {!t.is_default && (
            <Button size="small" onClick={() => handleSetDefault(t.id)}>Set as Default</Button>
          )}
          <Tooltip title="Edit columns and labels">
            <Button size="small" icon={<EditOutlined />} onClick={() => setEditTarget(t)}>Edit</Button>
          </Tooltip>
          <Tooltip title="Clone to create a custom copy">
            <Button size="small" icon={<CopyOutlined />} onClick={() => handleClone(t)} loading={cloning}>Clone</Button>
          </Tooltip>
          {!t.is_system && (
            <Popconfirm title="Delete this template?" onConfirm={() => handleDelete(t.id)} disabled={t.is_default}>
              <Tooltip title={t.is_default ? "Cannot delete the active default" : "Delete"}>
                <Button size="small" danger icon={<DeleteOutlined />} disabled={t.is_default} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      </div>

      {/* Column preview chips */}
      <div style={{ marginTop: 8 }}>
        <Space size={4} wrap>
          {(Array.isArray(t.columns) ? t.columns : [])
            .filter((c) => c.visible)
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            .map((c) => (
              <Tag key={c.key} style={{ fontSize: 10, fontFamily: "monospace" }}>
                {c.labels?.en || c.key}
              </Tag>
            ))}
        </Space>
      </div>
    </div>
  );

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <Title level={5} style={{ margin: 0 }}>{stageLabel}</Title>
        {systemTemplate && (
          <Button size="small" icon={<CopyOutlined />} onClick={() => handleClone(systemTemplate)} loading={cloning}>
            New Custom Template
          </Button>
        )}
      </div>

      {systemTemplate && templateCard(systemTemplate)}
      {customTemplates.map(templateCard)}

      {templates.length === 0 && (
        <Alert message="No template seeded yet. Click 'Seed Defaults' above." type="warning" showIcon />
      )}

      {editTarget && (
        <TemplateEditor
          template={editTarget}
          onSave={handleSave}
          onClose={() => setEditTarget(null)}
        />
      )}
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const PrintTemplateConsole = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listTemplates();
      setTemplates(data);
    } catch (err) {
      message.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      await seedDefaultTemplates();
      message.success("Default templates seeded");
      fetchTemplates();
    } catch (err) {
      message.error("Seed failed");
    } finally {
      setSeeding(false);
    }
  };

  const machineInstructionTemplates = templates.filter((t) => t.document_type === "machine_instructions");

  const tabItems = [
    {
      key: "machine_instructions",
      label: (
        <Space>
          <PrinterOutlined />
          Machine Instructions
          <Badge count={machineInstructionTemplates.length} style={{ backgroundColor: "#1890ff" }} />
        </Space>
      ),
      children: (
        <div>
          {STAGE_META.map((stage) => {
            const stageTemplates = machineInstructionTemplates.filter((t) => t.stage_code === stage.code);
            return (
              <StageSection
                key={stage.code}
                stageCode={stage.code}
                stageLabel={stage.label}
                templates={stageTemplates}
                onRefresh={fetchTemplates}
              />
            );
          })}
        </div>
      ),
    },
    {
      key: "invoice",
      label: <Space><PrinterOutlined />Invoice <Tag style={{ fontSize: 10 }}>Coming Soon</Tag></Space>,
      disabled: true,
      children: null,
    },
    {
      key: "challan",
      label: <Space><PrinterOutlined />Challan <Tag style={{ fontSize: 10 }}>Coming Soon</Tag></Space>,
      disabled: true,
      children: null,
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <PageTitle title="Back" subtitle="Print Template Console" />

      <Card
        title={
          <Space>
            <PrinterOutlined style={{ fontSize: 18 }} />
            <Title level={4} style={{ margin: 0 }}>Print Template Console</Title>
          </Space>
        }
        extra={
          <Space>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Configure columns, labels and ordering for every printed document
            </Text>
            <Button onClick={handleSeed} loading={seeding}>
              Seed / Reset Defaults
            </Button>
            <Button onClick={fetchTemplates} loading={loading}>Refresh</Button>
          </Space>
        }
      >
        <Alert
          message="How it works"
          description={
            <ul style={{ margin: "4px 0", paddingLeft: 20, fontSize: 12 }}>
              <li>Each stage has a <b>System Default</b> template seeded from the application's built-in column definitions.</li>
              <li>Clone any template to create a custom version — set it as the default to activate it.</li>
              <li>Each column has an <b>English primary label</b> and a <b>Hindi (हिंदी) label</b> used when printing in Hindi.</li>
              <li>Toggle column visibility, reorder with ↑↓ arrows, and set width per column.</li>
            </ul>
          }
          type="info"
          showIcon
          closable
          style={{ marginBottom: 20 }}
        />

        <Tabs items={tabItems} />
      </Card>
    </div>
  );
};

export default PrintTemplateConsole;
